import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { rolesService } from "../../services/rolesService";
import { Role } from "../../models/User";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router-dom";

type Props = {};

const ManageRoles: React.FC = (props: Props) => {
  const toastRef = useRef<Toast>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [roleData, setRoleData] = useState<Role>({
    id: "",
    role_code: "CUSTOMER",
    name: "",
    description: "",
    isActive: true,
  });

    const navigate = useNavigate();
    // const handlPermission = (rowData: any) => {
    //   navigate(`/admin/role-permission/${rowData.id}`);
    // };

    const handlPermission = (rowData: Role) => {
      navigate(`/admin/role-permission/${rowData.id}`, {
        state: { role: rowData },
      });
    };



  const dt = useRef<DataTable<Role[]>>(null);


  const openNew = () => {
    setIsEditMode(false);
    setRoleData({
      id: "",
      role_code: "CUSTOMER",
      name: "",
      description: "",
      isActive: true,
    });
    setDialogVisible(true);
  };

  const openEdit = (role: Role) => {
    setIsEditMode(true);
    setRoleData({ ...role });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setRoleData({
      id: "",
      role_code: "CUSTOMER",
      name: "",
      description: "",
      isActive: true,
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    role_code: Yup.string()
    .matches(/^[A-Z_0-9]+$/, "Only uppercase letters, digits, and underscores are allowed (no spaces or accents)")
    .required("Role code is required"),
    description: Yup.string().required("Description is required"),
    isActive: Yup.boolean().required("Status is required"),
  });

  const handleSaveRole = async (values: Role, { setSubmitting }: any) => {
    try {
      if (isEditMode) {
        await rolesService.updateRole(values);
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Role updated successfully",
        });
      } else {
        await rolesService.createRole(values);
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Role added successfully",
        });
      }
      fetchRoles();
      hideDialog();
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save role",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setIsInitializing(true);
      const res = await rolesService.getAllRoles();
      if (res.status === -1) {
        throw new Error(res.message || "Failed to fetch roles");
      }
      setRoles(res.data);
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch roles",
      });
    } finally {
      setIsInitializing(false);
    }
  };
  

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <div>
      <Toast ref={toastRef} />
      <Toolbar
        className="mb-4 rounded-md shadow-md"
        start={
          <div className="flex flex-wrap gap-2">
            <Button
              label="New"
              icon="pi pi-plus"
              severity="success"
              onClick={openNew}
              className="btn-primary"
            />
          </div>
        }
      />
      <DataTable
        ref={dt}
        value={roles ?? []}
        loading={isInitializing}
        paginator
        showGridlines
        stripedRows
        sortMode="multiple"
        removableSort
        scrollable
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks 
        NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} roles"
        emptyMessage="No roles found."
        tableClassName="text-sm"
      >
        <Column field="name" header="Name" sortable />
        <Column
          field="role_code"
          header="Role Code"
          sortable
          body={(rowData) => (
            <span className="capitalize">
              {rowData.role_code.toLowerCase()}
            </span>
          )}
        />
        <Column 
            field="description" 
            header="Description" 
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }} 
          />
        <Column
          field="status"
          header="Status"
          body={(rowData) => (
            <span
              className={`badge ${
                rowData.isActive ? "bg-green-500" : "bg-red-500"
              } text-white px-2 py-1 rounded`}
            >
              {rowData.isActive ? "Active" : "Inactive"}
            </span>
          )}
        />
        <Column
          header="Actions"
          body={(rowData) => (
            <div className="flex gap-1">
              <Button
                label="Edit"
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => openEdit(rowData)}
              />
               <Button
                label="Permissions"
                icon="pi pi-shield"
                className="p-button-rounded p-button-text"
                onClick={() => handlPermission(rowData)}
             />
            </div>
          )}
        />
      </DataTable>

      {/* Add/Edit Role Dialog */}
      <Dialog
        visible={dialogVisible}
        header={isEditMode ? "Edit Role" : "Add Role"}
        style={{ width: "500px" }}
        modal
        closeIcon="pi pi-times"
        onHide={hideDialog}
      >
        <Formik
          initialValues={roleData}
          validationSchema={validationSchema}
          onSubmit={handleSaveRole}
        >
          {({ isSubmitting, errors }) => (
            <Form className="flex flex-col gap-3">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm mb-1">
                  Name
                </label>
                <Field
                  id="name"
                  name="name"
                  as={InputText}
                  className={`w-full p-2 border rounded-md ${errors.name && errors.name ? "p-invalid border-red-500" : "border-gray-300"}`}
                />
                <ErrorMessage
                  name="name"
                  component="small"
                  className="p-error"
                />
              </div>

              {/* Role Code Field */}
              <div>
                <label htmlFor="role_code" className="block text-sm mb-1">
                  Role Code
                </label>
                <Field name="role_code">
                  {({ field, form }: any) => (
                    <InputText
                      {...field}
                      id="role_code"
                      className={`w-full p-2 border rounded-md ${
                        errors.role_code ? "p-invalid border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter a role code"
                      onChange={(e) => {
                        const upperValue = e.target.value.toUpperCase();
                        form.setFieldValue(field.name, upperValue);
                      }}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="role_code"
                  component="small"
                  className="p-error"
                />
              </div>

              {/* Description Field */}
              <div>
                <label htmlFor="description" className="block text-sm mb-1">
                  Description
                </label>
                <Field
                  id="description"
                  name="description"
                  as="textarea"
                  className={`w-full p-2 border rounded-md ${errors.description && errors.description ? "p-invalid border-red-500" : "border-gray-300"}`}
                />
                <ErrorMessage
                  name="description"
                  component="small"
                  className="p-error"
                />
              </div>

              {/* Status Field */}
              <div>
                <label htmlFor="isActive" className="block text-sm mb-1">
                  Status
                </label>
                <Field
                  name="isActive"
                  as={Dropdown}
                  options={[
                    { label: "Active", value: true },
                    { label: "Inactive", value: false },
                  ]}
                  className={`w-full border rounded-md ${errors.isActive && errors.isActive ? "p-invalid border-red-500" : "border-gray-300"}`}
                  placeholder="Select status"
                />
                <ErrorMessage
                  name="isActive"
                  component="small"
                  className="p-error"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end mt-4 gap-2">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={hideDialog}
                  type="button"
                />
                <Button
                  label="Save"
                  icon="pi pi-check"
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ManageRoles;
